import React, { useState, useEffect } from 'react';
import CourseCard from '../CourseCard/CourseCard';
import { coursesApi } from '../../services/api/courses.api';
import { ordersApi } from '../../services/api/orders.api';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Pagination } from 'swiper/modules';
import 'swiper/css/pagination';

// دالة لتنسيق التاريخ بشكل صحيح
const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    // تنسيق التاريخ بالعربية
    const day = date.getDate();
    const monthNames = [
      'يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيو',
      'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
    ];
    const month = monthNames[date.getMonth()];
    
    return `${day} ${month}`;
  } catch (err) {
    // خطأ في تنسيق التاريخ
    return '';
  }
};

const CoursesList = ({ categoryId, isMobile }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCourses, setUserCourses] = useState([]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        const params = {};
        
        // إضافة فلتر التصنيف إذا كان موجوداً
        if (categoryId) {
          params.category_id = categoryId;
        }
        
        const response = await coursesApi.getPublicCourses(params);
        // تم جلب الدورات بنجاح
        setCourses(response.data || []);
      } catch (err) {
        // خطأ في جلب الدورات
        setError('حدث خطأ أثناء تحميل الدورات');
      } finally {
        setLoading(false);
      }
    };
    
    // جلب دورات المستخدم المشترك فيها
    const fetchUserCourses = async () => {
      // التحقق من تسجيل دخول المستخدم بوجود token في sessionStorage
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        setUserCourses([]);
        return;
      }
      
      // التحقق من وجود البيانات في sessionStorage
      const cachedCourses = sessionStorage.getItem('dose_user_courses');
      
      if (cachedCourses) {
        try {
          const parsedCourses = JSON.parse(cachedCourses);
          setUserCourses(parsedCourses);
          return;
        } catch (err) {
          // خطأ في تحليل الدورات المخزنة
          // في حالة خطأ في التحليل، نستمر بجلب البيانات من السيرفر
        }
      }
      
      try {
        // استخدام API الموجودة بالفعل لجلب طلبات المستخدم
        const response = await ordersApi.getUserOrders('course');
        
        // نفلتر الطلبات المقبولة فقط
        const acceptedOrders = response.filter(order => order.status === 'accepted' || order.status === 'approved');
        
        // نجمع الدورات من الطلبات المقبولة
        const myCourses = acceptedOrders.flatMap(order => {
          return order.items.map(item => {
            const courseId = item.course_id || item.courseId || order.id;
            return courseId;
          });
        });
        
        // تخزين النتيجة في sessionStorage
        sessionStorage.setItem('dose_user_courses', JSON.stringify(myCourses));

        setUserCourses(myCourses);
      } catch (err) {
        // خطأ في جلب دورات المستخدم
        // في حالة الخطأ، نضع مصفوفة فارغة
        setUserCourses([]);
      }
    };

    fetchCourses();
    fetchUserCourses();
  }, [categoryId]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {[1, 2, 3].map((item) => (
          <div key={item} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse h-[490px]">
            <div className="h-48 bg-gray-300 dark:bg-gray-700"></div>
            <div className="p-4">
              <div className="h-6 bg-gray-300 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
              <div className="flex justify-between items-center mt-4">
                <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-1/4"></div>
                <div className="h-10 bg-gray-300 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error}</p>
      </div>
    );
  }

  if (courses.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p>لا توجد دورات متاحة حالياً</p>
      </div>
    );
  }

  return (
    <div className="courses-list-container">
      {isMobile ? (
        <div className="relative w-full">
          <Swiper
            modules={[Pagination]}
            slidesPerView={1}
            spaceBetween={10}
            pagination={{
              clickable: true,
              el: '.swiper-pagination-courses',
            }}
            style={{ width: '100%', paddingBottom: '30px' }}
          >
            {courses.map((course) => {
            // تنسيق التواريخ
            const formattedStartDate = formatDate(course.start_date);
            const formattedEndDate = formatDate(course.end_date);
            
            // تنسيق الأسعار
            let originalPrice = 'مجاني';
            let discountedPrice = 'مجاني';
            let discount = null;
            
            if (course.price && parseFloat(course.price) > 0) {
              // إذا كان هناك سعر أصلي
              // تحويل السعر لرقم صحيح بدون كسور عشرية
              const priceInt = parseInt(course.price);
              originalPrice = `${priceInt} ريال`;
              discountedPrice = originalPrice; // السعر المعروض هو نفس السعر الأصلي إذا لم يكن هناك خصم
              
              // إذا كان هناك سعر بعد الخصم
              if (course.discounted_price && parseFloat(course.discounted_price) > 0 && parseFloat(course.discounted_price) < parseFloat(course.price)) {
                // تحويل السعر المخفض لرقم صحيح بدون كسور عشرية
                const discountedPriceInt = parseInt(course.discounted_price);
                discountedPrice = `${discountedPriceInt} ريال`;
                
                // حساب نسبة الخصم إذا لم تكن موجودة
                if (!course.discount && course.price && course.discounted_price) {
                  const originalValue = parseFloat(course.price);
                  const discountedValue = parseFloat(course.discounted_price);
                  if (originalValue > 0) {
                    const discountPercent = Math.round(((originalValue - discountedValue) / originalValue) * 100);
                    discount = `${discountPercent}%`;
                  }
                } else if (course.discount) {
                  discount = `${course.discount}%`;
                }
              }
            }
            
            // تنسيق مسارات الصور
            const coverImagePath = course.cover_image 
              ? (course.cover_image.startsWith('http') 
                  ? course.cover_image 
                  : course.cover_image) // مسار الصورة كامل من الباك إند
              : 'https://picsum.photos/id/1018/1920/1080';
              
            const coursePlanPath = course.plan_image
              ? (course.plan_image.startsWith('http')
                  ? course.plan_image
                  : course.plan_image) // مسار خطة الدورة كامل من الباك إند
              : '';
            
            // التحقق مما إذا كان المستخدم مشترك في هذه الدورة
            const isSubscribed = userCourses.includes(course.id);
            
            return (
              <SwiperSlide key={course.id}>
                <CourseCard 
                  id={course.id}
                  title={course.name}
                  instructor={course.instructors?.[0]?.name || ''}
                  category={course.category?.name || ''}
                  originalPrice={originalPrice}
                  discountedPrice={discountedPrice}
                  discount={discount}
                  startDate={formattedStartDate}
                  endDate={formattedEndDate}
                  coursePlan={coursePlanPath}
                  coverImage={coverImagePath}
                  isSubscribed={isSubscribed}
                  isExpired={course.is_expired || false}
                  is_coming_soon_display={course.is_coming_soon_display || false}
                />
              </SwiperSlide>
            );
          })}
          </Swiper>
          
          {/* Swipe hint text */}
          
          {/* Custom pagination container */}
          <div className="swiper-pagination-courses" style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 0 8px',
            '--swiper-pagination-bullet-size': '14px',
            '--swiper-pagination-bullet-horizontal-gap': '2px',
            '--swiper-pagination-color': '#0ea5e9',
            '--swiper-pagination-bullet-inactive-color': 'rgb(209, 213, 219)',
            '--swiper-pagination-bullet-inactive-opacity': '1',
            '--swiper-pagination-bullet-opacity': '1',
            '--swiper-pagination-bullet-active-width': '8px',
            '--swiper-pagination-bullet-active-height': '8px',
            '--swiper-pagination-bullet-border-radius': '50%',
            '--swiper-pagination-bullet-transition': 'all 0.2s ease-in-out'
          }} />
          
          {/* Dark mode overrides for pagination */}
          <style>{`
            .dark .swiper-pagination-bullet {
              background: #9ca3af !important;
              opacity: 0.5 !important;
            }
            .dark .swiper-pagination-bullet-active {
              background: #0ea5e9 !important;
              opacity: 1 !important;
            }
          `}</style>
          
          <div className="flex justify-center">
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              اسحب يمين ويسار للتنقل
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            // تنسيق التواريخ
            const formattedStartDate = formatDate(course.start_date);
            const formattedEndDate = formatDate(course.end_date);
            
            // تنسيق الأسعار
            let originalPrice = 'مجاني';
            let discountedPrice = 'مجاني';
            let discount = null;
            
            if (course.price && parseFloat(course.price) > 0) {
              // إذا كان هناك سعر أصلي
              // تحويل السعر لرقم صحيح بدون كسور عشرية
              const priceInt = parseInt(course.price);
              originalPrice = `${priceInt} ريال`;
              discountedPrice = originalPrice; // السعر المعروض هو نفس السعر الأصلي إذا لم يكن هناك خصم
              
              // إذا كان هناك سعر بعد الخصم
              if (course.discounted_price && parseFloat(course.discounted_price) > 0 && parseFloat(course.discounted_price) < parseFloat(course.price)) {
                // تحويل السعر المخفض لرقم صحيح بدون كسور عشرية
                const discountedPriceInt = parseInt(course.discounted_price);
                discountedPrice = `${discountedPriceInt} ريال`;
                
                // حساب نسبة الخصم إذا لم تكن موجودة
                if (!course.discount && course.price && course.discounted_price) {
                  const originalValue = parseFloat(course.price);
                  const discountedValue = parseFloat(course.discounted_price);
                  if (originalValue > 0) {
                    const discountPercent = Math.round(((originalValue - discountedValue) / originalValue) * 100);
                    discount = `${discountPercent}%`;
                  }
                } else if (course.discount) {
                  discount = `${course.discount}%`;
                }
              }
            }
            
            // تنسيق مسارات الصور
            const coverImagePath = course.cover_image 
              ? (course.cover_image.startsWith('http') 
                  ? course.cover_image 
                  : course.cover_image) // مسار الصورة كامل من الباك إند
              : 'https://picsum.photos/id/1018/1920/1080';
              
            const coursePlanPath = course.plan_image
              ? (course.plan_image.startsWith('http')
                  ? course.plan_image
                  : course.plan_image) // مسار خطة الدورة كامل من الباك إند
              : '';
            
            // التحقق مما إذا كان المستخدم مشترك في هذه الدورة
            const isSubscribed = userCourses.includes(course.id);
            
            return (
              <CourseCard 
                key={course.id}
                id={course.id}
                title={course.name}
                instructor={course.instructors?.[0]?.name || ''}
                category={course.category?.name || ''}
                originalPrice={originalPrice}
                discountedPrice={discountedPrice}
                discount={discount}
                startDate={formattedStartDate}
                endDate={formattedEndDate}
                coursePlan={coursePlanPath}
                coverImage={coverImagePath}
                isSubscribed={isSubscribed}
                isExpired={course.is_expired || false}
                is_coming_soon_display={course.is_coming_soon_display || false}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoursesList;
